/**
 * Script de gestion des notifications push pour Master League
 * Ce script gère l'enregistrement du service worker et l'abonnement aux notifications push
 */

class PushNotificationManager {
    constructor() {
        this.swRegistration = null;
        this.isSubscribed = false;
        this.vapidPublicKey = null;
        this.pushButtonSelector = '#push-notification-toggle';
        this.pushStatusSelector = '#push-notification-status';
        this.pushIconSelector = '#push-notification-icon';
        
        // Couleurs thème Master League
        this.colors = {
            primary: '#000096',   // Bleu
            secondary: '#FFD700', // Or
            success: '#28a745',
            danger: '#dc3545'
        };
    }

    /**
     * Initialiser le gestionnaire de notifications push
     */
    async init() {
        try {
            // Vérifier si les notifications push sont supportées
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
                console.log('Les notifications push ne sont pas supportées par ce navigateur');
                this.updateUI(false, 'disabled');
                return;
            }

            // Récupérer la clé publique VAPID
            await this.getVapidPublicKey();

            // Enregistrer le service worker
            this.swRegistration = await navigator.serviceWorker.register('/service-worker.js');
            console.log('Service Worker enregistré avec succès:', this.swRegistration);

            // Vérifier l'état de l'abonnement
            await this.checkSubscription();

            // Ajouter les écouteurs d'événements
            this.addEventListeners();
            
            // Ajouter l'écouteur pour les messages du service worker
            this.setupMessageListener();
            
            // Initialiser le badge si l'API est disponible
            this.initBadge();
        } catch (error) {
            console.error('Erreur lors de l\'initialisation des notifications push:', error);
            this.updateUI(false, 'error');
        }
    }

    /**
     * Récupérer la clé publique VAPID depuis le serveur
     */
    async getVapidPublicKey() {
        try {
            const response = await fetch('/vapid-public-key');
            const data = await response.json();
            this.vapidPublicKey = data.publicKey;
        } catch (error) {
            console.error('Erreur lors de la récupération de la clé VAPID:', error);
            throw error;
        }
    }

    /**
     * Vérifier si l'utilisateur est déjà abonné aux notifications push
     */
    async checkSubscription() {
        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            this.isSubscribed = subscription !== null;
            this.updateUI(this.isSubscribed);
        } catch (error) {
            console.error('Erreur lors de la vérification de l\'abonnement:', error);
            this.updateUI(false, 'error');
        }
    }

    /**
     * S'abonner aux notifications push
     */
    async subscribe() {
        try {
            // Demander la permission pour les notifications
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                console.log('Permission pour les notifications refusée');
                this.updateUI(false, 'denied');
                return;
            }

            // Convertir la clé publique VAPID en Uint8Array
            const applicationServerKey = this.urlB64ToUint8Array(this.vapidPublicKey);

            // S'abonner aux notifications push
            const subscription = await this.swRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: applicationServerKey
            });

            console.log('Abonné aux notifications push:', subscription);

            // Envoyer l'abonnement au serveur
            await this.saveSubscription(subscription);

            this.isSubscribed = true;
            this.updateUI(true);
        } catch (error) {
            console.error('Erreur lors de l\'abonnement aux notifications push:', error);
            this.updateUI(false, 'error');
        }
    }

    /**
     * Se désabonner des notifications push
     */
    async unsubscribe() {
        try {
            const subscription = await this.swRegistration.pushManager.getSubscription();
            if (!subscription) {
                console.log('Aucun abonnement à supprimer');
                this.isSubscribed = false;
                this.updateUI(false);
                return;
            }

            // Supprimer l'abonnement du serveur
            await this.deleteSubscription(subscription);

            // Supprimer l'abonnement local
            await subscription.unsubscribe();

            console.log('Désabonné des notifications push');
            this.isSubscribed = false;
            this.updateUI(false);
        } catch (error) {
            console.error('Erreur lors du désabonnement des notifications push:', error);
        }
    }

    /**
     * Envoyer l'abonnement au serveur
     * @param {PushSubscription} subscription L'objet d'abonnement
     */
    async saveSubscription(subscription) {
        try {
            const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
            const response = await fetch('/push-subscriptions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': token,
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    endpoint: subscription.endpoint,
                    keys: {
                        p256dh: subscription.toJSON().keys.p256dh,
                        auth: subscription.toJSON().keys.auth
                    },
                    contentEncoding: (PushManager.supportedContentEncodings || ['aesgcm'])[0],
                    deviceInfo: JSON.stringify({
                        userAgent: navigator.userAgent,
                        platform: navigator.platform
                    })
                })
            });

            if (!response.ok) {
                throw new Error('Erreur lors de l\'enregistrement de l\'abonnement');
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement de l\'abonnement:', error);
            throw error;
        }
    }

    /**
     * Supprimer l'abonnement du serveur
     * @param {PushSubscription} subscription L'objet d'abonnement
     */
    async deleteSubscription(subscription) {
        try {
            const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
            const response = await fetch('/push-subscriptions', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': token,
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    endpoint: subscription.endpoint
                })
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la suppression de l\'abonnement');
            }

            return await response.json();
        } catch (error) {
            console.error('Erreur lors de la suppression de l\'abonnement:', error);
            throw error;
        }
    }

    /**
     * Mettre à jour l'interface utilisateur en fonction de l'état de l'abonnement
     * @param {boolean} isSubscribed Indique si l'utilisateur est abonné
     * @param {string} status Statut supplémentaire (disabled, denied, error)
     */
    updateUI(isSubscribed, status = null) {
        const pushButton = document.querySelector(this.pushButtonSelector);
        const pushStatus = document.querySelector(this.pushStatusSelector);
        const pushIcon = document.querySelector(this.pushIconSelector);

        if (!pushButton || !pushStatus || !pushIcon) {
            return;
        }

        // Mettre à jour le bouton et l'icône
        if (isSubscribed) {
            pushButton.textContent = 'Désactiver les notifications';
            pushButton.classList.remove('btn-primary');
            pushButton.classList.add('btn-secondary');
            pushIcon.classList.remove('fa-bell');
            pushIcon.classList.add('fa-bell-slash');
            pushStatus.textContent = 'Notifications activées';
            pushStatus.style.color = this.colors.success;
        } else {
            pushButton.textContent = 'Activer les notifications';
            pushButton.classList.remove('btn-secondary');
            pushButton.classList.add('btn-primary');
            pushIcon.classList.remove('fa-bell-slash');
            pushIcon.classList.add('fa-bell');
            
            // Statuts spéciaux
            if (status === 'disabled') {
                pushButton.disabled = true;
                pushStatus.textContent = 'Notifications non supportées';
                pushStatus.style.color = this.colors.danger;
            } else if (status === 'denied') {
                pushStatus.textContent = 'Notifications bloquées';
                pushStatus.style.color = this.colors.danger;
            } else if (status === 'error') {
                pushStatus.textContent = 'Erreur de configuration';
                pushStatus.style.color = this.colors.danger;
            } else {
                pushStatus.textContent = 'Notifications désactivées';
                pushStatus.style.color = '';
            }
        }
    }

    /**
     * Ajouter les écouteurs d'événements
     */
    addEventListeners() {
        const pushButton = document.querySelector(this.pushButtonSelector);
        if (pushButton) {
            pushButton.addEventListener('click', () => {
                if (this.isSubscribed) {
                    this.unsubscribe();
                } else {
                    this.subscribe();
                }
            });
        }
    }
    
    /**
     * Configurer l'écouteur de messages du service worker
     */
    setupMessageListener() {
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                console.log('[PushNotificationManager] Message reçu du service worker:', event.data);
                
                if (event.data && event.data.action === 'updateBadge') {
                    this.updateBadge(event.data.badgeCount);
                }
            });
        }
    }
    
    /**
     * Initialiser le badge sur l'icône de l'application
     */
    async initBadge() {
        try {
            // Vérifier si l'API Badging est disponible
            if ('setAppBadge' in navigator) {
                console.log('[PushNotificationManager] API Badging disponible');
                
                // Essayer de récupérer le nombre de notifications non lues depuis le serveur
                try {
                    const response = await fetch('/api/notifications/unread-count');
                    if (response.ok) {
                        const data = await response.json();
                        if (data.count > 0) {
                            this.updateBadge(data.count);
                        } else {
                            // Effacer le badge s'il n'y a pas de notifications non lues
                            this.clearBadge();
                        }
                    }
                } catch (error) {
                    console.error('[PushNotificationManager] Erreur lors de la récupération du nombre de notifications:', error);
                }
            } else {
                console.log('[PushNotificationManager] API Badging non supportée');
            }
        } catch (error) {
            console.error('[PushNotificationManager] Erreur lors de l\'initialisation du badge:', error);
        }
    }
    
    /**
     * Mettre à jour le badge sur l'icône de l'application
     * @param {number} count - Nombre à afficher sur le badge
     */
    async updateBadge(count) {
        try {
            if ('setAppBadge' in navigator) {
                await navigator.setAppBadge(count);
                console.log(`[PushNotificationManager] Badge mis à jour: ${count}`);
            }
        } catch (error) {
            console.error('[PushNotificationManager] Erreur lors de la mise à jour du badge:', error);
        }
    }
    
    /**
     * Effacer le badge sur l'icône de l'application
     */
    async clearBadge() {
        try {
            if ('clearAppBadge' in navigator) {
                await navigator.clearAppBadge();
                console.log('[PushNotificationManager] Badge effacé');
            }
        } catch (error) {
            console.error('[PushNotificationManager] Erreur lors de l\'effacement du badge:', error);
        }
    }

    /**
     * Convertir une chaîne base64 en Uint8Array
     * @param {string} base64String La chaîne base64 à convertir
     * @returns {Uint8Array} Le tableau d'octets
     */
    urlB64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}

// Initialiser le gestionnaire de notifications push lorsque le DOM est chargé
document.addEventListener('DOMContentLoaded', () => {
    const pushManager = new PushNotificationManager();
    pushManager.init();
});
